import { PermissionEnum } from '@Application/Security/Roles';
import FetchBeneficiaries from '@Application/Beneficiaries/Beneficiaries/Queries/FetchBeneficiaries';
import fetchBeneficiaries from '@Application/Beneficiaries/Beneficiaries/fetchBeneficiaries';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import BeneficiaryCard from '@UI/Pages/Beneficiaries/BeneficiaryList/BeneficiaryCard';
import beneficiaryFiltersSignal, {
	setBeneficiaryFilters,
} from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import BeneficiaryFilterView from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryFilterView';
import { openBeneficiarySidebar } from '@UI/Pages/Beneficiaries/Signals/beneficiarySidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import describeAgencyScope from '@UI/Pages/Beneficiaries/BeneficiaryList/Helpers/describeAgencyScope';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type BeneficiaryListItemView from '@UI/Store/Beneficiaries/Views/BeneficiaryListItemView';
import { Plus, TriangleAlert, Users as UsersIcon } from 'lucide-react';
import useBeneficiaryAccess from '@UI/Hooks/useBeneficiaryAccess';
import { css } from '@emotion/react';
import { theme } from '@Guidelines/Theme/theme';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { CACHE_DISABLED, useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, DataView as BeneficiariesDataView, type DataViewPageEvent } from '@verclam/ui-kit';
import SubjectListContainer from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectListContainer';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { dataViewGridPt } from '@Guidelines/Theme/commonPassthrough';

const scopeMentionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

/**
 * Owns the fetch and every loading/error/empty/nominal branch of the beneficiary list.
 *
 * The loading branch returns skeletons *before* rendering `DataView`, and is not delegated to its
 * `loading` prop: `DataView` still renders `emptyMessage` while `value` is empty, so handing it a
 * pending query paints "Aucun dossier" before the answer is known, then swaps the node out on the
 * next render. An empty state is an answer — it may only be drawn once one has arrived.
 *
 * The branch order is deliberate: `error !== null` is checked before `data === null`, never the
 * reverse, so an absent `data` is never read as a failure (`guides/state-stores.md`, "Ce que
 * `useStoreQuery` ne garantit pas").
 */
const BeneficiariesListContent: React.FC = () => {
	const dispatch = useDispatch();
	const filters = useSignalValue(beneficiaryFiltersSignal);
	const queryKey = filters.queryParams.toString();
	const { currentFacility } = useStrictCurrentFacility();
	const { user } = useStrictAuthenticatedUser();
	const { organizations } = useCurrentOrganization();
	const accountId = user.account?.id ?? null;
	const canSupervise = useBeneficiaryAccess().access?.canSupervise === true;

	const {
		data: beneficiaries,
		isLoading,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.beneficiariesStore.beneficiaries,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return;
			}

			return dispatch(fetchBeneficiaries(new FetchBeneficiaries(accountId, filters.queryParams)));
		},
		cache: CACHE_DISABLED,
		deps: [accountId, queryKey],
	});

	const handleResetFiltersClick = React.useCallback(() => {
		setBeneficiaryFilters(BeneficiaryFilterView.createDefault());
	}, []);

	const handlePageChange = React.useCallback((event: DataViewPageEvent) => {
		setBeneficiaryFilters(beneficiaryFiltersSignal.peek().setPageAndRows(event.page, event.rows));
	}, []);

	const handleNewBeneficiaryClick = React.useCallback(() => {
		openBeneficiarySidebar();
	}, []);

	const renderBeneficiaryCard = React.useCallback(
		(beneficiary: BeneficiaryListItemView) => <BeneficiaryCard beneficiary={beneficiary} />,
		[],
	);

	const scopeMention = React.useMemo(() => {
		const { label, isSingleFacility } = describeAgencyScope(filters.agencyScope, organizations);
		const target = canSupervise
			? `tous les dossiers de ${isSingleFacility ? 'cette agence' : 'ces agences'}`
			: 'les dossiers qui vous sont affectés';

		return `La recherche porte sur ${label} et sur ${target}.`;
	}, [filters.agencyScope, organizations, canSupervise]);

	/**
	 * `null` while the query is pending: `DataView` treats `loading` as an overlay, not as a branch —
	 * its content still renders on `pageItems.length === 0` alone, so an unconditional message would
	 * claim "Aucun dossier", call to action included, before the answer is known. An empty state is
	 * an answer, and may only be drawn once one has arrived.
	 */
	const emptyMessage = React.useMemo(() => {
		const style = {
			height: '100%',
		};

		// Holds the frame's height so the list does not collapse under the kit's loading overlay, and
		// says nothing: any wording here is a claim about a result that has not arrived yet.
		if (isLoading) {
			return <div style={style} />;
		}

		if (filters.hasActiveFilters()) {
			return (
				<EmptyState
					title="Aucun résultat"
					description={scopeMention}
					icon={<TriangleAlert size={28} />}
					action={
						<Button
							type="button"
							text
							label="Réinitialiser les filtres"
							onClick={handleResetFiltersClick}
						/>
					}
					style={style}
				/>
			);
		}

		return (
			<EmptyState
				title="Aucun dossier"
				description={`Créez le premier dossier de l'agence ${currentFacility.name}.`}
				icon={<UsersIcon size={28} />}
				action={
					<WithPermission permission={PermissionEnum.SUBJECT_WRITE}>
						<Button
							type="button"
							label="Nouveau bénéficiaire"
							icon={<Plus size={16} />}
							onClick={handleNewBeneficiaryClick}
						/>
					</WithPermission>
				}
				style={style}
			/>
		);
	}, [
		isLoading,
		filters,
		scopeMention,
		currentFacility.name,
		handleResetFiltersClick,
		handleNewBeneficiaryClick,
	]);

	if (accountId === null) {
		return <ErrorState title="Impossible de charger les dossiers" />;
	}

	if (error !== null) {
		return <ErrorState title="Impossible de charger les dossiers" onRetry={refetch} />;
	}

	return (
		<SubjectListContainer>
			<p css={scopeMentionStyle}>{scopeMention}</p>
			<BeneficiariesDataView
				emptyMessage={emptyMessage}
				loading={isLoading}
				value={beneficiaries?.list ?? []}
				layout="grid"
				dataKey="id"
				itemTemplate={renderBeneficiaryCard}
				lazy
				paginator
				pt={dataViewGridPt}
				first={filters.page * filters.rows}
				rows={filters.rows}
				rowsPerPageOptions={[10, 25, 50, 100]}
				totalRecords={beneficiaries?.totalRecords ?? 0}
				onPage={handlePageChange}
			/>
		</SubjectListContainer>
	);
};

export default BeneficiariesListContent;
